import {
  Component,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {TuiDialogService} from '@taiga-ui/core';
import {ImageCropperComponent, ImageCroppedEvent} from 'ngx-image-cropper';
import {Subscription} from 'rxjs';
import {AssetType, assetAllowedMimeTypes} from '@gatewee/types/asset';
import {AssetUploadService} from '@gatewee/services/asset-upload.service';
import {GateweeButtonComponent} from '../gatewee-button/gatewee-button.component';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';
import {padTransparentSafeAreaPng} from '../utils/image/pad-transparent-safe-area';

@Component({
  selector: 'gatewee-asset-upload',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ImageCropperComponent,
    GateweeButtonComponent,
    GateweeIconComponent,
  ],
  templateUrl: './gatewee-asset-upload.component.html',
  styleUrls: ['./gatewee-asset-upload.component.css'],
})
export class GateweeAssetUploadComponent implements OnDestroy {
  @Input({required: true}) control!: FormControl<string | null>;
  @Input({required: true}) assetType!: AssetType;
  @Input() aspectRatio?: number;
  @Input() resizeToWidth?: number;
  @Input() resizeToHeight?: number;
  @Input() label?: string;
  @Input() domainId?: number;
  @Input() transparentSafeAreaRatio?: number;

  @ViewChild('cropperDialogTpl')
  private readonly cropperDialogTpl!: TemplateRef<unknown>;

  private dialogSub?: Subscription;

  cropperImageFile?: File;
  croppedBlob: Blob | null = null;
  uploading = false;
  errorMsg: string | null = null;

  constructor(
    private readonly dialogService: TuiDialogService,
    private readonly uploadService: AssetUploadService,
  ) {}

  get allowedMimes(): string[] {
    return assetAllowedMimeTypes[this.assetType] ?? [];
  }

  get acceptAttr(): string {
    return this.allowedMimes.join(',');
  }

  get previewUrl(): string | null {
    return this.control?.value ?? null;
  }

  onFileChange(file: File | null, fileInput?: HTMLInputElement): void {
    if (fileInput) fileInput.value = '';
    this.errorMsg = null;

    if (!file) return;

    if (!this.allowedMimes.includes(file.type)) {
      this.errorMsg = `Formato inválido. Tipos aceitos: ${this.allowedMimes.join(
        ', ',
      )}`;
      return;
    }

    this.cropperImageFile = file;
    this.croppedBlob = null;

    if (this.aspectRatio && this.isCroppableImage(file)) {
      this.openCropperDialog();
    } else {
      this.doUpload(file);
    }
  }

  private isCroppableImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  private openCropperDialog(): void {
    this.dialogSub?.unsubscribe();
    this.dialogSub = this.dialogService
      .open(this.cropperDialogTpl, {
        label: 'Recortar imagem',
        size: 'l',
        closeable: true,
        dismissible: false,
      })
      .subscribe({
        complete: () => {
          this.cropperImageFile = undefined;
          this.croppedBlob = null;
        },
      });
  }

  onImageCropped(event: ImageCroppedEvent): void {
    this.croppedBlob = event.blob ?? null;
  }

  async confirmCrop(close: unknown): Promise<void> {
    if (!this.croppedBlob || !this.cropperImageFile) return;

    const mimeType = this.croppedBlob.type || this.cropperImageFile.type;
    const ext = this.cropperImageFile.name.split('.').pop() ?? 'png';
    const croppedFile = new File([this.croppedBlob], `cropped.${ext}`, {
      type: mimeType,
    });

    this.closeModal(close);

    const fileToUpload =
      this.transparentSafeAreaRatio && this.transparentSafeAreaRatio > 0
        ? await padTransparentSafeAreaPng(croppedFile, {
            safeAreaRatio: this.transparentSafeAreaRatio,
          })
        : croppedFile;

    this.doUpload(fileToUpload);
  }

  cancelCrop(close: unknown): void {
    this.cropperImageFile = undefined;
    this.croppedBlob = null;
    this.closeModal(close);
  }

  closeModal(close: unknown): void {
    if (typeof close === 'function') {
      (close as () => void)();
    } else if (
      close &&
      typeof close === 'object' &&
      'complete' in close &&
      typeof (close as {complete?: () => void}).complete === 'function'
    ) {
      (close as {complete: () => void}).complete();
    }
    this.dialogSub?.unsubscribe();
    this.dialogSub = undefined;
  }

  private doUpload(file: File): void {
    this.uploading = true;
    this.errorMsg = null;

    this.uploadService.upload(file, this.assetType, this.domainId).subscribe({
      next: result => {
        this.control.setValue(result.publicUrl);
        this.uploading = false;
        this.cropperImageFile = undefined;
      },
      error: () => {
        this.errorMsg = 'Erro ao fazer upload. Tente novamente.';
        this.uploading = false;
        this.cropperImageFile = undefined;
      },
    });
  }

  clearImage(): void {
    this.control.setValue(null);
    this.errorMsg = null;
  }

  ngOnDestroy(): void {
    this.dialogSub?.unsubscribe();
  }
}
